import { useState } from 'react';
import { TransactionsHistoryRequest } from 'vopay';
import { useApi } from '#shared/providers/Api';

// The one Transaction History fetch hook for shared (ID-13367), on the host-injected api via useApi().
// Replaces client-dashboard's three overlapping tuple hooks — useFetchTransactionsHistory (list),
// useFetchTransactionsFile (pdf/csv) and useTransactionHistoryExport (export scrape) — with a single
// Direct-Pattern hook: they all hit api.fetchTransactionsHistory, differing only in the request flags
// and what the caller does with the response. The consumer instantiates it once per independent result
// (the page keeps a list instance and an export instance) and owns the returned rows; the hook keeps
// only totalRecords, which the paginator reads. Logs once and rethrows; the caller surfaces the error.
export default function useFetchTransactionsHistory() {
  const api = useApi();
  const [fetching, setFetching] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  async function fetchTransactionsHistory(req: TransactionsHistoryRequest) {
    try {
      setFetching(true);

      const res = await api.fetchTransactionsHistory({
        ...req,
        Currency: req.Currency === 'All' ? undefined : req.Currency,
      });

      if (!res.Success) throw new Error(res.ErrorMessage);

      // The /account/transactions reference marks PaginationMetaData absent-able, and it is in fact
      // omitted on the export-only responses this same hook now drives (ExportPDF/CSV, scrape/json).
      // Dereferencing it blind threw a TypeError *after* the export file had already been fetched, so
      // fall back to the always-present NumberOfRecords.
      setTotalRecords(res.PaginationMetaData?.TotalRecords ?? res.NumberOfRecords);

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  return { fetching, totalRecords, fetchTransactionsHistory };
}
